import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, first } from 'rxjs/operators';
import { LocalStorageService } from "src/app/services/shared/local-storage/local-storage.service";
import { SessionService } from 'src/app/services/shared/session/session.service';
import { IMenuData } from 'src/app/interfaces/shared';
import { environment } from "src/environments/environment";
import { HostListener } from '@angular/core';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false
})
export class SidebarComponent implements OnInit {


  private readonly SIDEBAR_STATE_KEY = 'dm_sidebar_collapsed';
  private readonly SIDEBAR_EXPANDED_KEY = 'dm_sidebar_expanded_menu';

  isCollapsedSidebar: boolean = false;
  expandedMenuItems: Set<string> = new Set();


  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (window.innerWidth < 768) {
      this.isCollapsedSidebar = false; // Evita que se vea "DM" en lugar de iconos
    }
  }


  currentCountry: string = '';
  selectedFlow: string = '/int';


  isVip: boolean = false;
  isExternoRds: boolean = false;

  get menuItems(): IMenuData[] {
    return this.localStorageService?.getMenuConfig || [];
  }

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private sessionService: SessionService
  ) {

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {

    });
  }

  ngOnInit() {
    this.currentCountry = this.localStorageService.getRegionSeleccionada?.getCode() || '';
    this.isVip = this.localStorageService.getIsVip;
    this.isExternoRds = this.localStorageService.getIsExternoRDS;

    this.selectedFlow = this.isVip ? '/vip' : (this.isExternoRds ? '/rdsportal' : '/int');
    this.isCollapsedSidebar = this.getStoredCollapsedState();

    const hadStoredExpandedState = this.hasStoredExpandedMenuData();
    this.expandedMenuItems = this.getStoredExpandedMenu();

    if (!hadStoredExpandedState) {
      // Primera vez que se carga (sin historial guardado): se autoexpande
      // la categoría a la que pertenece la ruta activa, solo para no
      // esconderla de entrada. De ahí en adelante manda el toggle manual
      // del usuario, incluso si sigue navegando dentro de esa categoría.
      this.menuItems.forEach(item => {
        if (this.hasActiveSubItem(item)) {
          this.expandedMenuItems.add(item.title);
        }
      });
      this.setStoredExpandedMenu();
    }
  }

  toggleSidebar() {
    this.isCollapsedSidebar = !this.isCollapsedSidebar;

    if (this.isCollapsedSidebar) {
      this.expandedMenuItems.clear();
      this.setStoredExpandedMenu();
    }

    this.setStoredCollapsedState(this.isCollapsedSidebar);
  }

  private getStoredCollapsedState(): boolean {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return window.localStorage.getItem(this.SIDEBAR_STATE_KEY) === 'true';
  }

  private setStoredCollapsedState(value: boolean): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(this.SIDEBAR_STATE_KEY, String(value));
  }

  private getStoredExpandedMenu(): Set<string> {
    if (typeof window === 'undefined' || !window.localStorage) return new Set();
    try {
      const raw = window.localStorage.getItem(this.SIDEBAR_EXPANDED_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  }

  private hasStoredExpandedMenuData(): boolean {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return window.localStorage.getItem(this.SIDEBAR_EXPANDED_KEY) !== null;
  }

  private setStoredExpandedMenu(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(this.SIDEBAR_EXPANDED_KEY, JSON.stringify(Array.from(this.expandedMenuItems)));
  }


  routeMenuSelected(menuSelect: IMenuData, event?: Event) {
    if (event) event.stopPropagation();

    if (this.hasSubItems(menuSelect) && !this.isCollapsedSidebar) {
      this.toggleSubMenu(menuSelect.title);
      return;
    }


    if (this.hasSubItems(menuSelect) && this.isCollapsedSidebar) {
      // Se abre desde el sidebar (config colapsado): el sidebar queda
      // expandido y esto se persiste, para que no vuelva a colapsarse
      // al navegar a una de sus subopciones.
      this.isCollapsedSidebar = false;
      this.setStoredCollapsedState(false);
      this.toggleSubMenu(menuSelect.title);
      return;
    }


    if (!this.hasSubItems(menuSelect)) {
      this.navigateLogic(menuSelect);
    }
  }

  navigateLogic(menuSelect: IMenuData) {
    this.localStorageService.setMenuApp = menuSelect;
    const routeKey = this.isVip ? 'routeVip' : (this.isExternoRds ? 'routeRds' : 'route');

    if (menuSelect?.[routeKey]) {
      const path = `${menuSelect[routeKey]}/${this.currentCountry.toLowerCase()}`;
      console.log('Navegando a:', path);
      this.router.navigate([path]);
    } else {
      this.goHome();
    }
  }

  goRouter(route: string) {
    this.router.navigateByUrl(route);
  }

  goHome() {
    const homeUrl = this.isVip
      ? environment.URL_RETURN_APP_VIP
      : (this.isExternoRds ? environment.URL_RETURN_APP_RDS : environment.URL_RETURN_APP);
    this.router.navigateByUrl(homeUrl);
  }

  logoutSessionGoToHome() {
    this.sessionService.logoutSessionGoToHome();
  }



  hasSubItems(item: IMenuData): boolean {
    return item.subItems && item.subItems.length > 0;
  }

  toggleSubMenu(title: string) {
    if (this.expandedMenuItems.has(title)) {
      this.expandedMenuItems.delete(title);
    } else {
      this.expandedMenuItems.add(title);
    }
    this.setStoredExpandedMenu();
  }

  // Controla si el submenú está DESPLEGADO (memoria manual del usuario,
  // persistida entre navegaciones). No depende de si la ruta activa
  // pertenece a la categoría, para poder cerrarla aunque se esté dentro.
  isMenuExpanded(item: IMenuData): boolean {
    return this.expandedMenuItems.has(item.title);
  }

  // Controla el resaltado en azul del ítem padre: solo cuando la ruta
  // activa realmente pertenece a esta categoría (no por estar desplegada).
  hasActiveSubItem(item: IMenuData): boolean {
    if (!this.hasSubItems(item)) return false;
    return item.subItems.some(sub => this.isRouteActive(this.getRoutePath(sub)));
  }

  visibleItem(itemMenu: any): boolean {
    if (this.isVip) return !itemMenu.disabledVip;
    if (this.isExternoRds) return !itemMenu.disabledRds;
    return !itemMenu.disabled;
  }

  getRoutePath(item: IMenuData): string {
    const routeKey = this.isVip ? 'routeVip' : (this.isExternoRds ? 'routeRds' : 'route');
    const basePath = item[routeKey];
    if (!basePath) return '';


    return `${basePath}/${this.currentCountry.toLowerCase()}`;
  }


  isRouteActive(route: string): boolean {
    if (!route) return false;
    return this.router.url.includes(route);
  }

  onSubItemClick(subItem: IMenuData, event: Event) {
  event.stopPropagation();
  this.navigateLogic(subItem);
}
}
